/**
 * Pieces shared by the four auth pages, so each page file stays a flow and not
 * a pile of markup.
 */
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Card, CheckCircleIcon, Input, cx } from "../../components/ui";
import { checkPassword } from "../../lib/passwordPolicy";

export function AuthCard({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <Card className="animate-scale-in space-y-5 p-6 sm:p-8">
      <div className="space-y-1.5">
        <h1 className="font-display text-xl font-bold tracking-tight text-navy-950">{title}</h1>
        {subtitle ? <p className="text-sm text-slate-500">{subtitle}</p> : null}
      </div>
      {children}
      {footer ? <div className="border-t border-slate-100 pt-4 text-sm text-slate-500">{footer}</div> : null}
    </Card>
  );
}

/** Failures announce themselves; they sit above the action, never under it. */
export function AuthAlert({ children }: { children: ReactNode }) {
  return (
    <p
      role="alert"
      className="animate-fade-in rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-inset ring-red-600/15"
    >
      {children}
    </p>
  );
}

export function AuthNote({ children }: { children: ReactNode }) {
  return (
    <p className="animate-fade-in rounded-lg bg-brand-50 px-3 py-2 text-sm text-brandText ring-1 ring-inset ring-brand-600/20">
      {children}
    </p>
  );
}

/** A text link in the auth flow — signup from login, sign-in from reset. */
export function AuthLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link
      to={to}
      className="font-medium text-brandText underline-offset-2 transition-colors hover:text-brandText hover:underline"
    >
      {children}
    </Link>
  );
}

/** Emailed codes are six digits: one wide, scoreboard-style field. */
export function CodeField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <Input
      value={value}
      onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 6))}
      inputMode="numeric"
      autoComplete="one-time-code"
      pattern="[0-9]{6}"
      placeholder="000000"
      aria-label="Six-digit code"
      autoFocus
      required
      className="text-center font-display text-2xl tracking-[0.4em] tabular-nums"
    />
  );
}

/** The pool's password policy, shown as it is satisfied rather than after submit. */
export function PasswordChecklist({ password }: { password: string }) {
  const { rules } = checkPassword(password);
  return (
    <ul className="grid grid-cols-1 gap-1 sm:grid-cols-2">
      {rules.map((rule) => (
        <li
          key={rule.id}
          className={cx(
            "flex items-center gap-1.5 text-xs transition-colors",
            rule.met ? "text-brandText" : "text-slate-400",
          )}
        >
          <CheckCircleIcon className={cx("h-3.5 w-3.5", rule.met ? "opacity-100" : "opacity-40")} />
          {rule.label}
        </li>
      ))}
    </ul>
  );
}
